// Which attention cards this sign-in has put away, and why that is not a
// credential.
//
// THE TOKEN IS NOT HERE. session/store.ts is the only writer of the session,
// and it argues at length why that value lives under one key and nowhere else.
// This file writes a second key in the same kind of store: a list of card
// identities, keyed by `sessionId`. A stolen copy of this list tells an
// attacker which refusals somebody has already read. It does not let them
// read a report.
//
// Same tab store as the session, never the origin-wide one. The session
// itself is per tab, so a dismissal that outlived the sign-in would be a
// preference about a person rather than about this sitting. A fresh login
// is a new `sessionId` and the cards open again. A device that has never
// held this origin has an empty store and the cards are open. A reload of
// the same tab keeps them shut, which is the whole of what putting them
// away is for.
//
// THE STORE IS HANDED IN. Reaching for the browser object from here would
// be a second file that knows where the session lives, and check.mjs names
// store.ts as the only one that may. This module reads and writes a key.
// The signed-in tree hands it the same tab store the session already uses.
//
// THE SHAPE IS VALIDATED RATHER THAN CAST. The value came out of a store a
// person can edit. A missing `sessionId`, a non-array `keys`, or a key that
// is not a string all read as nothing put away, which opens the cards
// rather than trusting a half-written list.

import type { KeyedStore } from "./store.js";

/** The one key. Beside the session, never inside it. */
export const DISMISSED_KEY = "nova.xp.console.dismissed";

type Held = {
  readonly sessionId: string;
  readonly keys: readonly string[];
};

function parseHeld(raw: string): Held | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  const sessionId = row["sessionId"];
  const keys = row["keys"];
  if (typeof sessionId !== "string" || sessionId === "") return null;
  if (!Array.isArray(keys) || keys.some((key) => typeof key !== "string")) return null;
  return { sessionId, keys };
}

/** The cards this sign-in has already put away. Empty if the store disagrees. */
export function readDismissed(sessionId: string, storage: KeyedStore): Set<string> {
  let raw: string | null;
  try {
    raw = storage.getItem(DISMISSED_KEY);
  } catch {
    return new Set();
  }
  if (raw === null) return new Set();
  const held = parseHeld(raw);
  if (held === null || held.sessionId !== sessionId) return new Set();
  return new Set(held.keys);
}

/** Remember that this card is shut, for this sign-in only. */
export function writeDismissed(sessionId: string, id: string, storage: KeyedStore): void {
  try {
    const held = readDismissed(sessionId, storage);
    held.add(id);
    storage.setItem(DISMISSED_KEY, JSON.stringify({ sessionId, keys: [...held] }));
  } catch {
    // The card is still shut in React state for this mount.
  }
}
